import React, { useCallback, useRef, useState } from 'react';
import { UploadSimple, FilePdf, Image as ImageIcon, FileText } from '@phosphor-icons/react';

/**
 * UploadDropzone — premium drag-drop upload zone.
 *
 * Visual feedback:
 *   • idle → warm paper card with dashed border + UploadSimple
 *   • hovering (dragover) → teal accent glow + "Drop to add" copy
 *   • disabled (while uploading) → muted
 *
 * Delegates actual file handling to parent via `onFiles(File[])`.
 * Accepted types inherited from parent's `accept` string (default: docs + images).
 */
export const UploadDropzone = ({
  onFiles,
  disabled = false,
  accept = '.pdf,.jpg,.jpeg,.png,.webp,.heic',
  compact = false,
}) => {
  const inputRef = useRef(null);
  const [hovering, setHovering] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setHovering(false);
    if (disabled) return;
    const list = Array.from(e.dataTransfer?.files || []);
    if (list.length) onFiles(list);
  }, [onFiles, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setHovering(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setHovering(false);
  }, []);

  const handleSelect = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) onFiles(list);
    e.target.value = '';
  };

  const classes = [
    'relative rounded-2xl border-2 border-dashed transition-all duration-200',
    'flex flex-col items-center justify-center text-center',
    compact ? 'px-5 py-6' : 'px-6 py-10 sm:py-14',
    hovering
      ? 'border-[#2FB3A0] bg-[#2FB3A0]/[0.05]'
      : 'border-[#D6CFC1] bg-[#FBFAF6] hover:border-[#0E1C2E]/30 hover:bg-white',
    disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer',
  ].join(' ');

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      disabled={disabled}
      className={classes}
      data-testid="upload-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={handleSelect}
        className="hidden"
      />
      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
        hovering ? 'bg-[#2FB3A0] text-white' : 'bg-white text-[#0E1C2E] border border-[#ECE8E1]'
      }`}>
        <UploadSimple size={20} weight="bold" />
      </div>
      <p className="text-[15px] font-semibold text-[#0E1C2E] leading-snug">
        {hovering ? 'Drop to add' : 'Drop a report or tap to choose'}
      </p>
      <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed max-w-xs">
        Blood tests · pathology · imaging · scripts · referrals
      </p>
      {!compact && (
        <div className="flex items-center gap-4 mt-4 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><FilePdf size={13} weight="duotone" /> PDF</span>
          <span className="inline-flex items-center gap-1"><ImageIcon size={13} weight="duotone" /> JPG · PNG</span>
          <span className="inline-flex items-center gap-1"><FileText size={13} weight="duotone" /> HEIC</span>
        </div>
      )}
    </button>
  );
};

export default UploadDropzone;
